import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';

import { BehaviorSubject, fromEvent as observableFromEvent, merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';

import * as momentNs from 'moment-timezone';
const moment = momentNs;

import { WindowRefService } from '../../../shared/service/window/window.service';

export const TIMEZONE = 'Europe/London';

@Injectable()
export class CalendarSlideService {
    startX;
    startLeft;
    holder = 0;
    dragActive = false;
    drag: ElementRef;
    slots: any;
    width: number;
    end: number;
    slotWidth: number;
    click = new Subject();
    click$ = this.click;
    meridiem = new Subject<string>();
    meridiem$ = this.meridiem;
    disabled = false;
    arrows$ = new BehaviorSubject<{ left: boolean; right: boolean; }>(undefined);
    indexFromStart = 0;
    indexForNoon = 0;
    columns = 0;
    slotsWithDataWidth = 0;
    meridiemChosen: string = moment().tz(TIMEZONE).format('A');

    constructor(
        private windowRef: WindowRefService,
        @Inject(DOCUMENT) private document: any
    ) {
        observableFromEvent(this.windowRef.nativeWindow, 'resize')
            .pipe(
                debounceTime(1000),
                distinctUntilChanged()
            )
            .subscribe(_ => {
                this.resetCalendar();
            });
    }

    get constrain(): number {
        const calenderCol = this.document.querySelector('.js-sg-calendar__col--overflow-hidden');

        return (calenderCol) ? Math.round(calenderCol.getBoundingClientRect().width) : 0;
    }

    resetCalendar(resetStyle = true, columns?: number): void {
        if (typeof columns === 'number') this.setColumns(columns);
        if (resetStyle) this.drag.nativeElement.removeAttribute('style');
        setTimeout(_ => {
            this.disabled = false;
            this.setConfig(this.drag, resetStyle);
        }, 0);
    }

    setClick(): void {
        this.setConfig(undefined);
        this.click$.pipe(
            map((data: string) => this.setStep(data)))
            .subscribe(_ => {
                this.setTransform(this.holder);
            });
    }

    meridiemChange(data: string): void {
        this.meridiemChosen = data;
        if (data === 'AM') this.setTransform(this.setOffset(0));
        if (data === 'PM') this.setTransform(this.setOffset(this.indexForNoon));
    }

    setDrag(drag?: ElementRef, columns?: number): void {
        this.setColumns(columns);
        this.setConfig(drag);
        const mouseup$ = observableFromEvent(this.document, 'mouseup');
        const mousedown$ = observableFromEvent(drag.nativeElement, 'mousedown');
        const mousemove$ = observableFromEvent(this.document, 'mousemove');
        const touchstart$ = observableFromEvent(drag.nativeElement, 'touchstart');
        const touchend$ = observableFromEvent(this.document, 'touchend');
        const touchmove$ = observableFromEvent(this.document, 'touchmove');

        const mousedrag$ = mousedown$.pipe(
            filter(data => !this.disabled),
            tap((md: MouseEvent) => {
                md.preventDefault();

                // tslint:disable-next-line:max-line-length
                // https://developer.mozilla.org/en-US/docs/Web/API/Window/pageYOffset || https://developer.mozilla.org/it/docs/Web/API/Window/scrollX
                this.startX = md.clientX + this.windowRef.nativeWindow.pageXOffset;
                this.startLeft = this.holder || 0;
            }),
            mergeMap(data => {
                return mousemove$.pipe(map((mm: MouseEvent) => {
                    mm.preventDefault();

                    return this.startLeft + mm.clientX - this.startX;
                }),
                takeUntil(mouseup$));
            }));

        // Touch event handlers
        const touchdrag$ = touchstart$
            .pipe(
                filter(data => !this.disabled),
                tap((ts: TouchEvent) => {
                    ts.preventDefault();

                    // tslint:disable-next-line:max-line-length
                    // https://developer.mozilla.org/en-US/docs/Web/API/Window/pageYOffset || https://developer.mozilla.org/it/docs/Web/API/Window/scrollX
                    this.startX = ts.touches[0].clientX + this.windowRef.nativeWindow.pageXOffset;
                    this.startLeft = this.holder || 0;
                }),
                mergeMap(data => {
                    return touchmove$
                        .pipe(map((tm: TouchEvent) => {
                            tm.preventDefault();

                            return this.startLeft + tm.touches[0].clientX - this.startX;
                        }),
                        takeUntil(touchend$));
                })
            );

        const mouseTouch$ = merge(mousedrag$, touchdrag$);

        mouseTouch$.pipe(map(data => {
            if (data > 0) data = 0;
            if (this.end >= data) data = this.end;
            this.holder = data;

            return data;
        }),
        tap(_ => this.configArrows))
        .subscribe(pos => {
            this.setTransform(pos);
        });
    }

    setColumns(columns?: number): void {
        if (typeof columns === 'number') this.columns = columns;
    }

    getSlotWidth(): number {
        const oneSlot = this.slots[0];
        const styles = getComputedStyle(oneSlot);
        const margin = parseInt(styles.marginLeft, 10) + parseInt(styles.marginRight, 10);

        return oneSlot.getBoundingClientRect().width + margin || 0;
    }

    setConfig(drag, setOffset = true): void {
        if (drag) {
            this.drag = drag;
            this.slots = drag.nativeElement.querySelectorAll('.js-sg-calendar__row .js-sg-calendar__slot');
            this.slotWidth = this.getSlotWidth();
            this.slotsWithDataWidth = this.slotWidth * this.columns + 1;
            this.width = this.slotWidth * this.slots.length;
            this.end = this.constrain - this.slotWidth * this.columns + 1;
            this.drag.nativeElement.style.width = `${this.width}px`;
            this.configArrows(this.setOffset());
            if (setOffset) this.setTransform(this.setOffset());
            if (this.constrain > this.width || this.constrain > this.slotsWithDataWidth) this.disabled = true;
        }
    }

    setOffset(index = this.indexFromStart): number {
        let offsetX = (this.slotWidth * index === 0 || this.constrain > this.slotsWithDataWidth) ? 0 : -(this.slotWidth * index);
        if (this.meridiemChosen === 'PM' && this.indexForNoon > 0) {
            if (offsetX < this.end) {
                offsetX = this.end;
            }
        }

        if (this.width > this.end) {
            if (offsetX < this.end) {
                this.holder = 0;

                return 0;
            }
            this.holder = offsetX;

            return offsetX;
        }
    }

    configArrows(pos: number): void {
        if (this.width <= this.constrain) this.arrows$.next({ left: false, right: false });
        if (pos > this.end && this.width > this.constrain) this.arrows$.next({ left: true, right: true });
        if (pos <= this.end && this.width > this.constrain) this.arrows$.next({ left: true, right: false });
        if (pos === 0 && this.width > this.constrain) this.arrows$.next({ left: false, right: true });
        if (this.constrain > this.slotsWithDataWidth) this.arrows$.next({ left: false, right: false });
    }

    setTransform(pos: number): void {
        this.configArrows(pos);
        this.drag.nativeElement.style.transform = `translateX(${pos}px)`;
        this.selectMeridiem();
    }

    selectMeridiem(): void {
        const noon = -this.slotWidth * this.indexForNoon;

        if (this.columns) {
            if (noon < this.holder && !this.disabled) {
                this.meridiem.next('AM');
            }

            if (noon >= this.holder && !this.disabled) {
                this.meridiem.next('PM');
            }
        }
    }

    setStep(data: string): void {
        let futureResult;

        if (data === 'right' && !this.disabled) {
            futureResult = this.holder - this.slotWidth;
            this.holder = (futureResult > this.end) ? futureResult : this.end;
        }
        if (data === 'left' && !this.disabled) {
            futureResult = this.holder + this.slotWidth;
            this.holder = (futureResult < 0) ? futureResult : 0;
        }
    }
}
